/**
 * CampaignHierarchyView — the read-only face of CampaignHierarchy, for the Event Detail page.
 *
 * Same tree, same look: it reuses the editor's nested <ul>/<li> and connector lines
 * (CampaignHierarchy.css), so the hierarchy reads the same on both pages. What differs is
 * that there is nothing to type into — each row is an icon, the campaign id in monospace, and
 * a badge where the node is another Event Hub event rather than a bare SFDC campaign. An event row
 * also shows that event's name (from ListSubEvents — a node carries only the event id), linked to
 * its page when the host passes `onOpenEvent`. A child event made with Create Child Event has no
 * campaign id yet, so for it the name is the only label.
 *
 * Fully expanded, no toggles: on a detail page the point is to see the whole tree at once,
 * and real hierarchies are a handful of nodes. Hand-rendered for the reason the editor is —
 * Cloudscape's TreeView is not exported at this version.
 */

import Badge from '@amzn/awsui-components-react/polaris/badge';
import Box from '@amzn/awsui-components-react/polaris/box';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import Link from '@amzn/awsui-components-react/polaris/link';
import { countNodes, rootLabel, type HierarchyNode } from './campaignHierarchy';
import './CampaignHierarchy.css';

export interface CampaignHierarchyViewProps {
  eventName?: string;
  /** The event's own SFDC Campaign ID — the root's label. */
  campaignId?: string;
  nodes: readonly HierarchyNode[];
  /** Names of the events in the tree, by event id. */
  eventNames?: ReadonlyMap<string, string>;
  /** Opens an event's page; when absent, event names are plain text. */
  onOpenEvent?: (eventId: string) => void;
}

interface RowContext {
  eventNames?: ReadonlyMap<string, string>;
  onOpenEvent?: (eventId: string) => void;
}

function EventName({ eventId, ctx }: { eventId: string; ctx: RowContext }) {
  const name = ctx.eventNames?.get(eventId);
  if (!name) return null;
  if (!ctx.onOpenEvent) return <span>{name}</span>;
  const open = ctx.onOpenEvent;
  return (
    <Link
      href={`/event/events/${eventId}`}
      onFollow={(e) => {
        e.preventDefault();
        open(eventId);
      }}
    >
      {name}
    </Link>
  );
}

function Row({ node, ctx }: { node: HierarchyNode; ctx: RowContext }) {
  const name = node.eventId ? ctx.eventNames?.get(node.eventId) : undefined;
  return (
    <li className="eh-hierarchy__item">
      <div className="eh-hierarchy__row">
        <Icon name={node.children.length ? 'folder' : 'file'} variant="subtle" />
        {node.sfdcCampaignId || !name ? <Box variant="code">{node.sfdcCampaignId || '—'}</Box> : null}
        {node.eventId ? <EventName eventId={node.eventId} ctx={ctx} /> : null}
        {node.eventId ? <Badge color="blue">Event</Badge> : null}
      </div>
      {node.children.length ? (
        <ul className="eh-hierarchy__children">
          {node.children.map((c) => (
            <Row key={c.key} node={c} ctx={ctx} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function CampaignHierarchyView({
  eventName,
  campaignId,
  nodes,
  eventNames,
  onOpenEvent,
}: CampaignHierarchyViewProps) {
  const total = countNodes(nodes);
  const ctx: RowContext = { eventNames, onOpenEvent };
  return (
    <ul className="eh-hierarchy" aria-label="Event hierarchy">
      <li className="eh-hierarchy__item">
        <div className="eh-hierarchy__row eh-hierarchy__row--root">
          <Icon name="calendar" />
          <Box fontWeight="bold">{rootLabel(campaignId, eventName)}</Box>
          <Box color="text-body-secondary" fontSize="body-s">
            {total} {total === 1 ? 'child campaign' : 'child campaigns'}
          </Box>
        </div>
        <ul className="eh-hierarchy__children">
          {nodes.map((n) => (
            <Row key={n.key} node={n} ctx={ctx} />
          ))}
        </ul>
      </li>
    </ul>
  );
}
